import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  Show,
} from 'solid-js';
import {
  FileFolderItem,
  MissionDetailsSelection,
  RightPanelTabName,
  RunDetailsSelection,
  useCapabilityRegistryContext,
  useApiServices,
  useProjectSettings,
} from '@mobile-mapping/providers';
import { ViewDetailsResponse } from '@data-access/connect/tc-tcps';
import {
  CreateViewTab,
  MenuPosition,
  VerticalTab,
  VerticalTabPanel,
  ViewDetailsTab,
} from '@trimble/ui-components/solidjs';
import { t } from '@trimble/i18n';
import { MeasurementDetailsPanel } from '@trimble/measurements-details';
import { PolylineMeasurementCapability } from '@trimble/web3d-capabilities';
import { MeasurementsCapabilityProvider } from '@trimble/web3d-providers';
import { SettingsTab } from './SettingsTab';
import { ModelDetails } from './ModelDetails';
import { MissionDetailsContainer } from './MissionDetailsContainer';
import { RunDetails } from './RunDetails';
import { useCreateView } from './use-create-view';
import { useConnectProject } from '@trimble/connect-project-providers';
import { ObjectPropertiesTab } from './ObjectPropertiesTab';
import { LayoutTab } from './LayoutTab';

interface RightPanelDataMap {
  [RightPanelTabName.ViewDetails]: ViewDetailsResponse;
  [RightPanelTabName.ModelDetails]: {
    projectExplorerUrl: string;
    model: FileFolderItem;
  };
  [RightPanelTabName.RunDetails]: RunDetailsSelection;
  [RightPanelTabName.MissionDetails]: MissionDetailsSelection;
  [RightPanelTabName.CreateView]: undefined;
  [RightPanelTabName.MeasurementDetails]: undefined;
  [RightPanelTabName.ObjectProperties]: undefined;
  [RightPanelTabName.Layout]: undefined;
  [RightPanelTabName.Settings]: undefined;
}

// Export type for the active tab with or without typed data property
export type ActiveTab = {
  [K in keyof RightPanelDataMap]: RightPanelDataMap[K] extends undefined
    ? { tab: K }
    : { tab: K; data: RightPanelDataMap[K] };
}[keyof RightPanelDataMap];

export interface RightPanelProps {
  activeTab?: ActiveTab;
  onTabChange?(tab?: RightPanelTabName): void;
}

export const RightPanel: Component<RightPanelProps> = (props) => {
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const { formattingSettings, alignmentSettings } = useProjectSettings();
  const { tcTcpsApi } = useApiServices();
  const { selectedRegion } = useConnectProject();
  const createViewProps = useCreateView();

  const measurementsProvider = capabilityRegistry.get(
    MeasurementsCapabilityProvider
  );
  const polylineMeasurementCapability = capabilityRegistry.get(
    PolylineMeasurementCapability
  );

  const [activeTab, setActiveTab] = createSignal<ActiveTab | undefined>(
    undefined
  );

  const handleTabChange = (tab: RightPanelTabName, isOpen: boolean): void => {
    const setTab = isOpen ? tab : undefined;
    props.onTabChange?.(setTab);
  };

  const handleTabClose = (): void => {
    props.onTabChange?.(undefined);
  };

  const tabs = createMemo<VerticalTab<RightPanelTabName>[]>(() => [
    {
      name: RightPanelTabName.ViewDetails,
      content: () => {
        const data = createMemo(() => {
          const tab = activeTab();
          return tab?.tab === RightPanelTabName.ViewDetails
            ? tab.data
            : undefined;
        });

        return (
          <Show when={data()} keyed>
            {(data) => (
              <ViewDetailsTab
                view={data}
                tcTcpsApi={tcTcpsApi}
                region={selectedRegion}
                onClose={handleTabClose}
              />
            )}
          </Show>
        );
      },
    },
    {
      name: RightPanelTabName.ModelDetails,
      content: () => {
        const data = createMemo(() => {
          const tab = activeTab();
          return tab?.tab === RightPanelTabName.ModelDetails
            ? tab.data
            : undefined;
        });

        return (
          <Show when={data()} keyed>
            {(data) => <ModelDetails {...data} onClose={handleTabClose} />}
          </Show>
        );
      },
    },
    {
      name: RightPanelTabName.RunDetails,
      content: () => {
        const data = createMemo(() => {
          const tab = activeTab();
          return tab?.tab === RightPanelTabName.RunDetails
            ? tab.data
            : undefined;
        });

        return (
          <Show when={data()} keyed>
            {(data) => <RunDetails run={data} onClose={handleTabClose} />}
          </Show>
        );
      },
    },
    {
      name: RightPanelTabName.MissionDetails,
      content: () => {
        const data = createMemo(() => {
          const tab = activeTab();
          return tab?.tab === RightPanelTabName.MissionDetails
            ? tab.data
            : undefined;
        });

        return (
          <Show when={data()} keyed>
            {(data) => (
              <MissionDetailsContainer
                mission={data}
                onClose={handleTabClose}
              />
            )}
          </Show>
        );
      },
    },
    {
      name: RightPanelTabName.CreateView,
      tooltip: {
        text: t('mobileMapping.views.create.title'),
        position: 'left' as const,
      },
      menuIcon: 'tc-icon-plus',
      menuIconVisible: activeTab()?.tab === RightPanelTabName.CreateView,
      content: () => (
        <CreateViewTab {...createViewProps} onClose={handleTabClose} />
      ),
    },
    {
      name: RightPanelTabName.MeasurementDetails,
      content: () => (
        <Show when={measurementsProvider()} keyed>
          {(provider) => (
            <MeasurementDetailsPanel
              measurement={provider.selectedMeasurement()}
              measurementsProvider={provider}
              // TODO: Uncomment when views support symbol saving
              // pointSymbols={pointSymbols}
              polylineMeasurementCapability={polylineMeasurementCapability()}
              baseLengthUnit={provider.lengthUnit}
              baseAreaUnit={provider.areaUnit}
              angleUnit={provider.angleUnit}
              formattingSettings={formattingSettings()}
              alignmentSettings={alignmentSettings()}
              onClose={handleTabClose}
              isMeasurementSaved={true}
            />
          )}
        </Show>
      ),
    },
    {
      name: RightPanelTabName.ObjectProperties,
      content: () => <ObjectPropertiesTab onClose={handleTabClose} />,
    },
    {
      name: RightPanelTabName.Layout,
      content: () => <LayoutTab onClose={handleTabClose} />,
    },
    {
      name: RightPanelTabName.Settings,
      content: () => <SettingsTab onClose={handleTabClose} />,
    },
  ]);

  createEffect(() => {
    setActiveTab(props.activeTab);
  });

  return (
    <VerticalTabPanel
      tabs={tabs()}
      menuVisible={false}
      menuPosition={MenuPosition.RIGHT}
      selectedTab={activeTab()?.tab}
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      enableDarkMode={true}
      onTabToggle={handleTabChange}
    />
  );
};
